import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { MdSecurity, MdAccessTime, MdVolumeUp, MdRefresh, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { toast } from "react-toastify";

// RiskOtpDialog Component - Displays backend OTP as CAPTCHA image
export function RiskOtpDialog({
  open,
  onClose,
  onVerify,
  onResend,
  otp,
  setOtp,
  loading,
  resendDisabled,
  countdown,
  otpStatus,
  phoneNumber,
  devOtp, // This is the OTP from backend
  theme,
}) {
  const darkMode = theme?.mode === "dark";
  
  // CAPTCHA states - ADD THESE STATE DECLARATIONS
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const [showCaptchaValue, setShowCaptchaValue] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [distortionLevel] = useState("medium");
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  
  // Reset CAPTCHA input when dialog opens or OTP changes
  useEffect(() => {
    if (open) {
      setCaptchaInput("");
      setCaptchaError(false);
      setVerificationAttempted(false);
    }
  }, [open, devOtp]);
  
  // Generate distorted CAPTCHA image from the backend OTP
  const generateCaptchaImage = (otpValue) => {
    if (!otpValue) return null;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 280;
    canvas.height = 80;
    
    // Background
    ctx.fillStyle = darkMode ? '#1e293b' : '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add noise (random dots)
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 0.5})`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
    
    // Add random lines
    for (let i = 0; i < 8; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.lineWidth = 1 + Math.random() * 2;
      ctx.stroke();
    }
    
    // Draw OTP text with distortion
    const distortionIntensity = distortionLevel === "low" ? 0.2 : distortionLevel === "medium" ? 0.4 : 0.6;
    const otpString = otpValue.toString();
    
    ctx.font = `bold ${36 - (distortionIntensity * 10)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Add wave distortion to each character
    for (let i = 0; i < otpString.length; i++) {
      const x = 50 + (i * 35) + Math.sin(i * 0.8) * (distortionIntensity * 15);
      const y = 40 + Math.cos(i * 0.5) * (distortionIntensity * 10);
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.sin(i) * distortionIntensity);
      
      // Random colors for each character
      const hue = Math.random() * 360;
      ctx.fillStyle = `hsl(${hue}, 70%, 50%)`;
      
      // Add shadow for 3D effect
      ctx.shadowBlur = 3;
      ctx.shadowColor = "rgba(0,0,0,0.3)";
      
      ctx.fillText(otpString[i], 0, 0);
      ctx.restore();
    }
    
    // Add more noise overlay
    ctx.globalCompositeOperation = 'source-over';
    for (let i = 0; i < 100; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.3})`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 1, 1);
    }
    
    return canvas.toDataURL();
  };
  
  // Get CAPTCHA image from backend OTP
  const captchaImage = devOtp && otpStatus === "sent" ? generateCaptchaImage(devOtp) : null;
  
  // Voice assistance function - reads the OTP
  const speakCaptcha = () => {
    if (!devOtp) return;
    
    // Stop any ongoing speech
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    setIsSpeaking(true);
    
    // Format the OTP for better pronunciation
    const otpString = devOtp.toString();
    const formattedText = otpString.split('').join(' . ') + ' . Verification code';
    
    const utterance = new SpeechSynthesisUtterance(formattedText);
    utterance.rate = 0.7;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    const speak = () => {
      const voices = window.speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => voice.name.includes('Google UK English Female') || voice.name.includes('Samantha'));
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      window.speechSynthesis.speak(utterance);
    };
    
    if (window.speechSynthesis.getVoices().length) {
      speak();
    } else {
      window.speechSynthesis.onvoiceschanged = speak;
    }
    
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
  };
  
  const handleVerify = async () => {
    // Prevent multiple verification attempts
    if (verificationAttempted || loading) {
      console.log("⚠️ Verification already in progress or attempted");
      return;
    }
    
    // Check if CAPTCHA input is valid
    if (!captchaInput || captchaInput.length !== 6) {
      setCaptchaError(true);
      toast.error("Please enter the 6-digit verification code");
      return;
    }
    
    // Check if entered CAPTCHA matches the backend OTP
    if (captchaInput !== devOtp?.toString()) {
      setCaptchaError(true);
      toast.error("Incorrect verification code. Please try again.");
      setCaptchaInput("");
      return;
    }
    
    setVerificationAttempted(true);
    setCaptchaError(false);
    
    // Pass the OTP directly to onVerify
    console.log("✅ Calling onVerify with OTP:", captchaInput);
    await onVerify(captchaInput);
  };
  
  const handleRefreshCaptcha = () => {
    setCaptchaInput("");
    setCaptchaError(false);
    setVerificationAttempted(false);
    
    const captchaElement = document.querySelector('.captcha-canvas');
    if (captchaElement) {
      captchaElement.style.transform = 'scale(0.95)';
      setTimeout(() => {
        captchaElement.style.transform = 'scale(1)';
      }, 200);
    }
    
    toast.info("CAPTCHA image refreshed");
  };
  
  const handleResendClick = async () => {
    setCaptchaInput("");
    setCaptchaError(false);
    setVerificationAttempted(false);
    await onResend();
  };
  
  const getStatusMessage = () => {
    switch (otpStatus) {
      case "sending":
        return "Generating OTP...";
      case "sent":
        return "Enter the verification code shown in the image below";
      case "verified":
        return "OTP verified successfully!";
      case "expired":
        return "OTP has expired. Please request a new one.";
      default:
        return "For your security, please verify your identity";
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          minWidth: "420px",
          maxWidth: "480px",
          overflow: "hidden",
          backgroundColor: darkMode ? "#1e293b" : "#ffffff",
          fontFamily: theme?.fontFamily || "Inter, sans-serif",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pb: 1,
          pt: 3,
          backgroundColor: darkMode ? "#0f172a" : "#f8fafc",
          borderBottom: darkMode ? "1px solid #334155" : "1px solid #e2e8f0",
        }}
      >
        <div className="flex flex-col items-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
            style={{
              backgroundColor: darkMode ? "#334155" : "#dbeafe",
            }}
          >
            <MdSecurity size={32} style={{ color: theme?.primaryColor || "#3b82f6" }} />
          </div>
          <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? "#f1f5f9" : "#1e293b" }}>
            Security Verification
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: darkMode ? "#94a3b8" : "#64748b" }}>
            {getStatusMessage()}
          </Typography>
        </div>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, mb: 1 }}>
        <div className="space-y-4">
          {/* CAPTCHA Box */}
          {devOtp && otpStatus === "sent" && captchaImage && (
            <div
              className="p-4 rounded-lg"
              style={{
                backgroundColor: darkMode ? "#0f172a" : "#f8fafc",
                border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: darkMode ? "#94a3b8" : "#6b7280",
                  mb: 2,
                  textAlign: "center",
                  fontSize: "11px",
                  letterSpacing: "1px",
                  fontWeight: 600,
                }}
              >
                🔐 VERIFICATION CODE
              </Typography>
              
              <div 
                className="captcha-canvas relative rounded-lg overflow-hidden mb-3 transition-transform"
                style={{ 
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  msUserSelect: "none",
                }}
                onContextMenu={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              >
                <img 
                  src={captchaImage} 
                  alt="Verification Code" 
                  style={{ 
                    width: "100%", 
                    height: "auto",
                    display: "block"
                  }}
                  draggable={false}
                />
              </div>
              
              <div className="flex justify-center gap-2 mb-3">
                <Tooltip title="Listen to verification code">
                  <IconButton
                    onClick={speakCaptcha}
                    disabled={isSpeaking}
                    size="small"
                    sx={{
                      color: theme?.primaryColor || "#3b82f6",
                      backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                      '&:hover': {
                        backgroundColor: darkMode ? "#334155" : "#f1f5f9",
                      }
                    }}
                  >
                    <MdVolumeUp size={20} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Refresh CAPTCHA image">
                  <IconButton
                    onClick={handleRefreshCaptcha}
                    size="small"
                    sx={{
                      color: theme?.primaryColor || "#3b82f6",
                      backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                      '&:hover': {
                        backgroundColor: darkMode ? "#334155" : "#f1f5f9",
                      }
                    }}
                  >
                    <MdRefresh size={20} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Show verification code (accessibility)">
                  <IconButton
                    onClick={() => setShowCaptchaValue(!showCaptchaValue)}
                    size="small"
                    sx={{
                      color: theme?.primaryColor || "#3b82f6",
                      backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                      '&:hover': {
                        backgroundColor: darkMode ? "#334155" : "#f1f5f9",
                      }
                    }}
                  >
                    {showCaptchaValue ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                  </IconButton>
                </Tooltip>
              </div>
              
              {showCaptchaValue && (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    mb: 2,
                    p: 1,
                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    fontSize: "18px",
                    letterSpacing: "4px",
                    fontWeight: "bold",
                    color: theme?.primaryColor || "#3b82f6"
                  }}
                >
                  {devOtp}
                </Typography>
              )}
              
              {isSpeaking && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    color: theme?.primaryColor || "#3b82f6",
                    mt: 1,
                    fontSize: "10px",
                  }}
                >
                  🔊 Speaking verification code...
                </Typography>
              )}
            </div>
          )}
          
          {/* CAPTCHA Input Field */}
          <div className="text-center">
            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "#cbd5e1" : "#475569" }}>
              Enter the verification code shown above
            </Typography>
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter 6-digit code"
              value={captchaInput}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value.length <= 6) {
                  setCaptchaInput(value);
                  setCaptchaError(false);
                  setVerificationAttempted(false);
                }
              }}
              disabled={otpStatus === "verified" || loading}
              error={captchaError}
              helperText={captchaError ? "Incorrect code. Please try again." : "Enter the 6-digit code from the image"}
              inputProps={{
                style: {
                  textAlign: "center",
                  fontSize: "24px",
                  letterSpacing: "8px",
                  fontFamily: "monospace",
                },
                maxLength: 6,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: darkMode ? "#0f172a" : "#ffffff",
                },
              }}
            />
          </div>
          
          {/* Verify Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={loading || captchaInput.length !== 6 || otpStatus === "verified" || verificationAttempted}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 600,
              backgroundColor: theme?.buttonColor || "#3b82f6",
              "&:hover": {
                backgroundColor: theme?.buttonColor ? `${theme.buttonColor}dd` : "#2563eb",
              },
            }}
          >
            {loading ? "Verifying..." : "Verify Code"}
          </Button>
          
          {/* Resend section */}
          {otpStatus !== "verified" && (
            <div className="text-center">
              <Button
                variant="text"
                onClick={handleResendClick}
                disabled={resendDisabled || loading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: darkMode ? "#94a3b8" : "#64748b",
                }}
              >
                {resendDisabled ? `Resend OTP in ${countdown}s` : "Resend OTP"}
              </Button>
              
              {resendDisabled && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <MdAccessTime size={12} />
                  <Typography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#64748b" }}>
                    Code valid for 10 minutes
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            px: 4,
            py: 1,
            borderRadius: "10px",
            fontWeight: 600,
            borderColor: darkMode ? "#475569" : "#cbd5e1",
            color: darkMode ? "#94a3b8" : "#64748b",
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}